import React, { Component, Fragment } from 'react';
import Select from 'react-select';

import PlusMultIcon from '../../icons/PlusMultIcon';
import Modal from '../../common/Modal';
import LargeCheckmarkIcon from '../../icons/LargeCheckmarkIcon';
import UpDownArrowIcon from '../../icons/UpDownArrowIcon';
import InputErrors from '../../common/InputErrors';
import permissionsOptions from '../../../utils/permissionsOptions';

class InviteUserModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      incompleteError: false,
      noInvitationsError: false,
      invalidEmailError: false,
      serverErrors: [],
      invitations: [{
        email: '',
        permissions: '',
      }],
      showSuccess: false,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { invitations } = this.state;
    // If we added an invitation, focus the latest email input so they can type
    if (invitations.length > prevState.invitations.length) {
      const emailInputs = document.querySelectorAll('.form__invitation-inputs input[name=email]');
      emailInputs[emailInputs.length - 1].focus();
    }
  }

  handleChange = (e, i) => {
    const { invitations } = this.state;
    const { name, value } = e.target;
    const newInvitations = [...invitations];
    newInvitations[i][name] = value;

    this.setState({ invitations: newInvitations });
  }

  handleSelectChange = ({ value }, i) => {
    const { invitations } = this.state;
    const newInvitations = [...invitations];
    newInvitations[i].permissions = value;
    this.setState({ invitations: newInvitations });
  }

  addInvitation = () => {
    this.setState(prevState => ({
      invitations: [...prevState.invitations, {
        email: '',
        permissions: '',
      }],
    }));
  }

  removeInvitation = (index) => {
    const { invitations } = this.state;
    const newInvitations = [...invitations];
    newInvitations.splice(index, 1);
    this.setState({ invitations: newInvitations });
  }

  submit = (e) => {
    const { invitations } = this.state;
    const { usersActions } = this.props;
    const emailInputs = [...document.querySelectorAll('.form__invitation input[type=email]')];
    e.preventDefault();
    this.resetErrors();
    if (!invitations.length || invitations.every(invitation => !invitation.email && !invitation.permissions)) {
      // We have no invitations entered yet
      this.setState({ noInvitationsError: true });
    } else if (emailInputs.some(input => !input.checkValidity())) {
      // One or more of the emails is invalid
      this.setState({ invalidEmailError: true });
    } else if (invitations.some(invitation => !invitation.email || !invitation.permissions)) {
      // One or more of the fields is empty
      this.setState({ incompleteError: true });
    } else {
      // We're all good; send it
      usersActions.invite({
        invitations: invitations.map(invitation => ({
          email: invitation.email,
          roleId: parseInt(invitation.permissions, 10),
        })),
      }, this.onSuccess, this.onFail);
    }
  }

  onSuccess = () => {
    this.setState({
      showSuccess: true,
      invitations: [],
    }, () => {
      document.querySelector('.ReactModal__Content:not(:empty)').focus(); // keep focus inside the modal
    });
  }

  onFail = (error) => {
    this.setState({
      serverErrors: error.response
        ? error.response.data
        : ['There was an error inviting a new user.'],
    });
  }

  resetErrors = () => {
    this.setState({
      incompleteError: false,
      noInvitationsError: false,
      invalidEmailError: false,
      serverErrors: [],
    });
  }

  closeModal = () => {
    const { showSuccess } = this.state;
    const { toggleInviteUserModal } = this.props;
    if (showSuccess) {
      this.setState({
        invitations: [],
        showSuccess: false,
      });
    }
    this.resetErrors();
    toggleInviteUserModal();
  }

  render() {
    const { serverErrors, incompleteError, noInvitationsError, invalidEmailError, showSuccess, invitations } = this.state;
    const { visible } = this.props;
    // Aggregate error messages
    let errors = serverErrors;
    if (incompleteError) errors = [...errors, 'All fields are required.'];
    if (noInvitationsError) errors = [...errors, 'Please enter at least one invitation.'];
    if (invalidEmailError) errors = [...errors, 'One or more email addresses is invalid.'];

    return (
      <Modal
        toggleModal={this.closeModal}
        visible={visible}
        centered
      >
        {showSuccess ? (
          <div className="text">
            <h2>
              An Invitation Has Been Sent
            </h2>
            <div className="modal__checkmark-icon">
              <LargeCheckmarkIcon />
            </div>
            <button
              type="button"
              onClick={this.closeModal}
              className="button"
            >
              Back to Account
            </button>
          </div>
        ) : (
          <Fragment>
            <div className="text text--small">
              <h2>
                Invite a New User
              </h2>
              <p className="modal__left-text">
                Please enter an email address and select an access level below to invite a colleague.
              </p>
              <ul className="modal__left-text">
                <li>Administrator accounts can change permissions for other users and receive email notifications about activity in the tool.</li>
                <li>Editors are allowed to create and update assessments.</li>
                <li>Viewers can view assessments but cannot edit or upload assessments.</li>
              </ul>
              <p className="modal__left-text">
                Note: Once a user has been made an Administrator, they cannot be demoted to a lower permissions level.
              </p>
            </div>
            <form className="form form--modal form--invite">
              {errors.length !== 0 && (
                <InputErrors errors={errors} />
              )}
              {invitations.map((invitation, i) => (
                <div className="form__invitation" key={i}>
                  <div className="form__invitation-inputs">
                    <label
                      className="form__text-label"
                      htmlFor={`input-invite-email-${i}`}
                    >
                      Email
                    </label>
                    <input
                      id={`input-invite-email-${i}`}
                      name="email"
                      value={invitations[i].email}
                      onChange={e => this.handleChange(e, i)}
                      type="email"
                    />
                    <label className="form__text-label" htmlFor={`permissions-${i}`}>
                      Permissions
                    </label>
                    <Select
                      onChange={e => this.handleSelectChange(e, i)}
                      value={invitations[i].permissions}
                      className="form__select"
                      name="permissions"
                      searchable={false}
                      clearable={false}
                      required
                      menuPlacement="auto"
                      menuShouldScrollIntoView
                      id={`permissions-${i}`}
                      arrowRenderer={({ isOpen }) => (
                        <UpDownArrowIcon direction={isOpen ? 'asc' : 'desc'} />
                      )}
                      options={permissionsOptions.filter(option => (
                        option.value !== '4' // remove deactivated option
                      ))}
                    />
                  </div>
                  <button
                    type="button"
                    className="form__remove-button"
                    onClick={() => this.removeInvitation(i)}
                  >
                    <PlusMultIcon option="small" rotated />
                  </button>
                </div>
              ))}
              <button onClick={this.addInvitation} type="button" className="form__add-button">
                <PlusMultIcon option="small" />
                Add Users
              </button>
              <div className="form__submit-button-wrapper">
                <button
                  type="submit"
                  className="button"
                  onClick={this.submit}
                >
                  Send Invitation
                </button>
              </div>
            </form>
          </Fragment>
        )}
      </Modal>
    );
  }
}

export default InviteUserModal;



// WEBPACK FOOTER //
// ./src/components/features/account/InviteUserModal.js